import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import React, { useEffect, useState } from 'react';
import * as ActionTypes from '../constants/performanceMetricsTableActions';
import * as MetricTitles from '../constants/performanceMetricsTableColumnTitles';
import ErrorMessage from '@shared/components/ErrorMessage';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import StatusSelect from './StatusSelect';
import MuiInputDecimal from '@shared/components/MuiInputDecimal';
import _ from 'lodash';
import { PERFORMANCE_METRIC_STATUSES } from '@shared/constants/checkpoint-performance-report-metric-enums';
import usePerformanceMetricsTableColumns from '../../hooks/usePerformanceMetricsTableColumns';
import { FieldNames } from '../constants/performanceMetricsTableFieldNames';
import StyledTable from '@shared/components/StyledTable';
import { metricProperties } from '../performanceMetric/pageSettings/pageSettingsByPageMode';
import { resetTableFieldsMap } from '../constants/resetTableFieldsMap';

/**
 * @typedef {object} PerformanceMetricsTableProps
 * @property {string} [viewMode]
 * @property {number} [orgUnitId]
 * @property {number} [checkPointId]
 * @property {number} [appraisalId]
 * @property {Array<*>} performanceMetrics
 * @property {Array<*>} [invalidMetrics]
 * @property {(performanceMetric: Object) => void} [handleChange]
 * @property {(tempId: number) => void} [handleEditPerformanceMetric]
 * @property {boolean} loading
 */

/**
 * @param {PerformanceMetricsTableProps} props
 */
const PerformanceMetricsTable = props => {
  const {
    handleChange,
    handleEditPerformanceMetric,
    loading,
    performanceMetrics,
    invalidMetrics = [],
    viewMode = PAGE_VIEW_MODES.ADD,
    orgUnitId = null,
    checkPointId = null,
    appraisalId = null
  } = props;

  const TOTAL_ROW_NUMBER = 25;
  const [tableData, setTableData] = useState([]);
  const metricErrorMessages = invalidMetrics.flatMap(metric => metric.errorMessages);

  useEffect(() => {
    if (!performanceMetrics) {
      return;
    }

    const data = _.sortBy(performanceMetrics, pm => pm.measurementName.toLowerCase()).map(
      (metric, index) => ({
        ...metric,
        action: viewMode === PAGE_VIEW_MODES.VIEW ? ActionTypes.VIEW : ActionTypes.EDIT,
        key: `row-${index}`,
        checkPointId,
        appraisalId
      })
    );

    if (viewMode === PAGE_VIEW_MODES.VIEW) {
      setTableData(data);
      return;
    }

    const emptyRows = [];
    for (let i = 0; i < TOTAL_ROW_NUMBER - data.length; i++) {
      emptyRows.push({
        measurementName: '',
        upperLimit: '',
        lowerLimit: '',
        actual: '',
        notes: '',
        status: '',
        action: ActionTypes.ADD,
        key: `empty-row-${i}`
      });
    }

    setTableData([...data, ...emptyRows]);
  }, [appraisalId, checkPointId, performanceMetrics, viewMode]);

  const handleChangeStatus = (value, performanceMetric) => {
    resetFieldsByStatus(value, performanceMetric);

    onChange(FieldNames.status, value, performanceMetric.tempId);
  };

  const resetFieldsByStatus = (value, performanceMetric) => {
    const referencDatapoint = performanceMetric.referenceCheckpointDatapoint;
    const fields = resetTableFieldsMap(referencDatapoint).get(value);
    for (const field of fields) {
      onChange(field.field, field.value, performanceMetric.tempId);
    }
    resetKeyTable(performanceMetric.tempId);
  };

  const resetKeyTable = tempId => {
    const data = [...tableData];
    const item = data.find(pm => pm.tempId === tempId);
    item.key = null;
    setTableData(data);
  };

  const handleChangeNotes = (value, tempId) => {
    onChange(FieldNames.notes, value, tempId);
  };

  const handleChangeNumber = (fieldName, event, tempId) => {
    const { value } = event.target;
    onChange(fieldName, value ? parseFloat(value) : null, tempId);
  };

  const isFieldValid = (tempId, columnTitle) => {
    if (!invalidMetrics.length) {
      return false;
    }
    const invalidMetric = invalidMetrics.find(metric => metric.tempId === tempId);
    return invalidMetric && invalidMetric.invalidFields.includes(columnTitle);
  };

  const getFieldClasses = (disabled, invalid) => {
    if (disabled) {
      return ' disabled';
    }
    if (invalid) {
      return ' invalid';
    }
    return '';
  };

  const onChange = (field, value, tempId) => {
    const performanceMetric = performanceMetrics.find(pm => pm.tempId === tempId);
    performanceMetric[field] = value;

    handleChange(performanceMetric);
  };

  const renderSelect = (value, performanceMetric, isNewMetric) => {
    if (!value) {
      return '-';
    }
    return (
      <StatusSelect
        value={value}
        performanceMetric={performanceMetric}
        handleChange={handleChangeStatus}
        disabled={isNewMetric}
      />
    );
  };

  const renderNumberInput = (columnTitle, value, index, placeholder, status, fieldName) => {
    if (!status) {
      return '-';
    }
    const disabled = status !== PERFORMANCE_METRIC_STATUSES.Update.Id;
    const invalid = isFieldValid(index, columnTitle);
    const classes = getFieldClasses(disabled, invalid);

    return (
      <MuiInputDecimal
        dataTestId={`input-number-${fieldName}`}
        className={'input-number' + classes}
        handleOnChange={e => handleChangeNumber(fieldName, e, index)}
        disabled={disabled}
        value={value}
        customStyle={{ padding: '1rem' }}
        placeholder={placeholder}
      />
    );
  };

  const renderNotesTextInput = (value, tempId, placeholder, status) => {
    if (!status) {
      return '-';
    }
    const disabled = status === PERFORMANCE_METRIC_STATUSES.NA.Id;
    const invalid = isFieldValid(tempId, MetricTitles.Notes);
    const classes = getFieldClasses(disabled, invalid);
    const maxLength = metricProperties.find(x => x.field === FieldNames.notes).maxLength;

    return (
      <MuiInputTextWithRemainingChars
        data-testid={`input-text-${FieldNames.notes}`}
        className={'input-text' + classes}
        type='text'
        defaultValue={value}
        onChange={event => handleChangeNotes(event.target.value, tempId)}
        disabled={disabled}
        disableUnderline
        placeholder={placeholder}
        style={{
          padding: '1rem'
        }}
        maxLength={maxLength}
        isInlineTable={true}
      />
    );
  };

  const renderTitle = () =>
    `Performance Report Metrics: ${performanceMetrics.length}${
      viewMode !== PAGE_VIEW_MODES.VIEW ? ` of ${TOTAL_ROW_NUMBER}` : ''
    }`;

  const columns = usePerformanceMetricsTableColumns(
    orgUnitId,
    appraisalId,
    viewMode,
    renderNumberInput,
    renderNotesTextInput,
    renderSelect,
    handleEditPerformanceMetric,
    checkPointId
  );

  return (
    <div className='performance-metrics-table' data-testid='performance-metrics-table'>
      <p className='bold'>{renderTitle()}</p>
      {metricErrorMessages.map((metricErrorMessage, index) => {
        return <ErrorMessage key={index} className='error-message' message={metricErrorMessage} />;
      })}
      <StyledTable
        data-testid='metric-table'
        columns={columns}
        dataSource={tableData}
        pagination={false}
        showSorterTooltip={false}
        loading={loading}
        onRow={(_, rowIndex) => ({
          'data-testid': `row-${rowIndex}`
        })}
      />
    </div>
  );
};

export default PerformanceMetricsTable;
